import { api, ApiError, STATUSES } from '../api'
import type { CanvasPoint, Idea, IdeaPatch, NewIdea, Placement, Status } from '../api'
import { guessChannels, guessFormat } from '../domain/guess'
import { createStore, useStore } from './store'
import { toast, toastError } from './toast'

/**
 * The idea list every screen reads, and the optimistic writes to it.
 *
 * A status change or a new idea shows up the instant it is made and is
 * corrected by the server's answer when it arrives. The phone is often on a
 * poor connection when an idea arrives, and waiting on a round trip before the
 * row appears is exactly the friction that stops ideas being written down.
 */
export interface IdeasState {
  ideas: Idea[]
  phase: 'loading' | 'ready' | 'error' | 'signed-out'
  error: string | null
}

export const ideasStore = createStore<IdeasState>({ ideas: [], phase: 'loading', error: null })

export const useIdeas = () => useStore(ideasStore)

/** Ideas not yet confirmed by the server have negative ids. */
export const isPending = (idea: Pick<Idea, 'id'>) => idea.id < 0

let nextTempId = -1
/** How many writes are in flight per idea; only the last answer is trusted. */
const inflight = new Map<number, number>()

/**
 * Per idea, whether a write is on its way or the last one failed, for the
 * editor's "Saving…" line. An idea whose writes all landed is not in it.
 */
export const savingStore = createStore<ReadonlyMap<number, 'saving' | 'failed'>>(new Map())

function trackInflight(id: number, change: 1 | -1, failed = false): void {
  const left = (inflight.get(id) ?? 0) + change
  if (left > 0) inflight.set(id, left)
  else inflight.delete(id)
  savingStore.set((was) => {
    const next = new Map(was)
    if (left > 0) next.set(id, 'saving')
    else if (failed) next.set(id, 'failed')
    else next.delete(id)
    return next
  })
}

function patchState(update: (ideas: Idea[]) => Idea[]): void {
  ideasStore.set((state) => ({ ...state, ideas: sortIdeas(update(state.ideas)) }))
}

export function sortIdeas(ideas: Idea[]): Idea[] {
  return [...ideas].sort(
    (a, b) => STATUSES.indexOf(a.status) - STATUSES.indexOf(b.status) || a.position - b.position,
  )
}

export function topPosition(ideas: Idea[], status: Status): number {
  const positions = ideas.filter((idea) => idea.status === status).map((idea) => idea.position)
  return (positions.length ? Math.min(...positions) : 0) - 1
}

export async function loadIdeas(): Promise<void> {
  try {
    const ideas = await api.listIdeas()
    ideasStore.set({ ideas: sortIdeas(ideas), phase: 'ready', error: null })
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthenticated) {
      ideasStore.set((state) => ({ ...state, phase: 'signed-out', error: null }))
      return
    }
    ideasStore.set((state) => ({
      ...state,
      phase: state.ideas.length ? 'ready' : 'error',
      error: error instanceof Error ? error.message : 'Could not load ideas',
    }))
  }
}

/** Put a server copy into the list (after a fetch of one idea, say). */
export function upsertIdea(idea: Idea): void {
  if (inflight.get(idea.id)) return
  patchState((ideas) =>
    ideas.some((item) => item.id === idea.id)
      ? ideas.map((item) => (item.id === idea.id ? idea : item))
      : [...ideas, idea],
  )
}

/**
 * A new idea, shown at once. `canvas` is where on the desktop canvas it was
 * started (a double-click in the Ideas frame); the server takes a place only
 * on a change, so it follows the create.
 */
export function createIdea(input: NewIdea & { status?: Status; canvas?: CanvasPoint }): Promise<Idea | null> {
  const { status, canvas, ...fields } = input
  const title = fields.title.trim()
  const now = new Date().toISOString()
  const tempId = nextTempId--
  const current = ideasStore.get().ideas
  const format = fields.format ?? guessFormat(title, fields.channels)
  const optimistic: Idea = {
    id: tempId,
    title,
    series: fields.series ?? 'profit',
    format,
    channels: fields.channels ?? guessChannels(format),
    posted_to: [],
    status: status ?? 'idea',
    body: fields.body ?? '',
    script: [],
    canvas: canvas ?? null,
    source: fields.source ?? 'phone',
    reels: {},
    published_url: null,
    short_caption: '',
    youtube_title: '',
    youtube_description: '',
    position: topPosition(current, status ?? 'idea'),
    created_at: now,
    updated_at: now,
  }
  patchState((ideas) => [...ideas, optimistic])

  return (async () => {
    try {
      let saved = await api.createIdea({ ...fields, title })
      if ((status && status !== saved.status) || canvas) {
        saved = await api.updateIdea(saved.id, { ...(status && { status }), ...(canvas && { canvas }) })
      }
      patchState((ideas) => ideas.map((item) => (item.id === tempId ? saved : item)))
      return saved
    } catch (error) {
      patchState((ideas) => ideas.filter((item) => item.id !== tempId))
      toastError(error, 'Could not save the idea')
      return null
    }
  })()
}

export async function updateIdea(
  id: number,
  patch: IdeaPatch,
  { undo }: { undo?: string } = {},
): Promise<Idea | null> {
  const before = ideasStore.get().ideas.find((idea) => idea.id === id)
  if (!before) return null
  const previous: IdeaPatch = {}
  const keys = new Set(Object.keys(patch) as (keyof IdeaPatch)[])
  // The server moves status and posting together (every video channel posted
  // is published), so an undo of either puts both back as they were. A status
  // change also forgets the card's place on the canvas, so its undo sends the
  // place back with it.
  if (keys.has('status') || keys.has('posted_to')) {
    keys.add('status')
    keys.add('posted_to')
    keys.add('canvas')
  }
  for (const key of keys) Object.assign(previous, { [key]: before[key] })
  // A status change drops the idea at the top of its new section, which is
  // where the eye goes to confirm it moved, and off its place on the canvas,
  // as the server does: the place belonged to the old stage's frame.
  const movedTo = patch.status !== undefined && patch.status !== before.status ? patch.status : null
  const local: IdeaPatch = {
    ...patch,
    ...(movedTo && patch.position === undefined && { position: topPosition(ideasStore.get().ideas, movedTo) }),
    ...(movedTo && patch.canvas === undefined && { canvas: null }),
  }

  patchState((ideas) => ideas.map((item) => (item.id === id ? { ...item, ...local } : item)))
  trackInflight(id, 1)
  if (undo) {
    toast(undo, { action: { label: 'Undo', run: () => void updateIdea(id, previous) } })
  }

  let failed = false
  try {
    const saved = await api.updateIdea(id, patch)
    if (inflight.get(id) === 1) {
      patchState((ideas) => ideas.map((item) => (item.id === id ? saved : item)))
    }
    return saved
  } catch (error) {
    // Put back only the fields this write changed and nobody has changed since.
    patchState((ideas) =>
      ideas.map((item) => {
        if (item.id !== id) return item
        const reverted = { ...item }
        for (const key of Object.keys(local) as (keyof IdeaPatch)[]) {
          if (item[key] === local[key]) Object.assign(reverted, { [key]: before[key] })
        }
        return reverted
      }),
    )
    toastError(error, 'Could not save the change')
    failed = true
    return null
  } finally {
    trackInflight(id, -1, failed)
  }
}

/**
 * Place many cards at once, all or nothing: the drag that pins a frame, or
 * Tidy up. Shown at once, put back if the server refuses.
 */
export async function arrangeIdeas(placements: Placement[]): Promise<void> {
  if (!placements.length) return
  const before = new Map(ideasStore.get().ideas.map((idea) => [idea.id, idea.canvas]))
  const next = new Map(placements.map((placement) => [placement.id, placement.canvas]))
  patchState((ideas) => ideas.map((item) => (next.has(item.id) ? { ...item, canvas: next.get(item.id) ?? null } : item)))
  // Only the places are this write's: a title typed meanwhile is left alone.
  try {
    const saved = new Map((await api.arrange(placements)).map((idea) => [idea.id, idea]))
    patchState((ideas) =>
      ideas.map((item) => {
        const answer = saved.get(item.id)
        return answer ? { ...item, canvas: answer.canvas, updated_at: answer.updated_at } : item
      }),
    )
  } catch (error) {
    patchState((ideas) =>
      ideas.map((item) =>
        next.has(item.id) && item.canvas === next.get(item.id) ? { ...item, canvas: before.get(item.id) ?? null } : item,
      ),
    )
    toastError(error, 'Could not move the cards')
  }
}

export async function deleteIdea(id: number): Promise<boolean> {
  const snapshot = ideasStore.get().ideas.find((idea) => idea.id === id)
  if (!snapshot) return false
  patchState((ideas) => ideas.filter((item) => item.id !== id))
  try {
    await api.deleteIdea(id)
    return true
  } catch (error) {
    patchState((ideas) => [...ideas, snapshot])
    toastError(error, 'Could not delete the idea')
    return false
  }
}

/** Reflect a server-side change to several ideas at once (a batch marked filmed). */
export function markLocally(ids: number[], patch: IdeaPatch): void {
  const wanted = new Set(ids)
  patchState((ideas) => ideas.map((item) => (wanted.has(item.id) ? { ...item, ...patch } : item)))
}
